import React from "react";
import { useTranslation } from "react-i18next";
import {
  useRetrieveProjectsElementsTags,
  useRetrieveProjectsTags,
  useRetrieveResourcesTags,
} from "@planzone/view-models";
import { VisibilitySwitch } from "@common/visibilitySwitch";
import { Button } from "@common/button";
import { Props } from "./types";
import styles from "./styles.scss";

function sanitize(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function hashCode(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return hash;
}

function intToRGB(i: number) {
  const c = (i & 0x00ffffff).toString(16).toUpperCase();
  return "00000".substring(0, 6 - c.length) + c;
}

function isColorLight(color: string) {
  const hex = color.replace("#", "");
  const c_r = parseInt(hex.substring(0, 2), 16);
  const c_g = parseInt(hex.substring(2, 2 + 2), 16);
  const c_b = parseInt(hex.substring(4, 4 + 2), 16);
  const brightness = (c_r * 299 + c_g * 587 + c_b * 114) / 1000;
  return brightness > 155;
}

function TagForm({ filters = [], type = "elements", submit }: Props) {
  const { t } = useTranslation();
  const {
    isRequestPending: isElementsRequestPending,
    isRequestSuccess: isElementsRequestSuccess,
    isRequestFailure: isElementsRequestFailure,
    tags: elementsTags,
    retrieveProjectsElementsTags,
  } = useRetrieveProjectsElementsTags();
  const {
    isRequestPending: isProjectsRequestPending,
    isRequestSuccess: isProjectsRequestSuccess,
    isRequestFailure: isProjectsRequestFailure,
    tags: projectsTags,
    retrieveProjectsTags,
  } = useRetrieveProjectsTags();
  const {
    isRequestPending: isResourcesRequestPending,
    isRequestSuccess: isResourcesRequestSuccess,
    isRequestFailure: isResourcesRequestFailure,
    tags: resourcesTags,
    retrieveResourcesTags,
  } = useRetrieveResourcesTags();
  const [formData, setFormData] = React.useState("");
  const { show, hide, isVisible } = VisibilitySwitch.useVisibilitySwitch(true);
  const tags =
    type === "resources"
      ? resourcesTags
      : type === "projects"
        ? projectsTags
        : elementsTags;
  const unUsedTags = tags.filter(
    (tag) => !filters.find(({ name }) => tag.name === name),
  );
  const filteredTags = unUsedTags.filter((tag) =>
    new RegExp(sanitize(formData), "i").test(tag.name),
  );

  React.useEffect(() => {
    !isElementsRequestPending &&
      !isElementsRequestSuccess &&
      !isElementsRequestFailure &&
      retrieveProjectsElementsTags({});
  }, []);

  React.useEffect(() => {
    !isProjectsRequestPending &&
      !isProjectsRequestSuccess &&
      !isProjectsRequestFailure &&
      retrieveProjectsTags();
  }, []);

  React.useEffect(() => {
    !isResourcesRequestPending &&
      !isResourcesRequestSuccess &&
      !isResourcesRequestFailure &&
      retrieveResourcesTags();
  }, []);

  return (
    <div className={styles.form}>
      <div className={styles.main}>
        <input
          type="text"
          className={styles.input}
          placeholder={t("listsList.tags.placeholder")}
          value={formData}
          onChange={({ target }) => setFormData(target.value)}
          onKeyUp={(e) => {
            if (e.key === "Enter") {
              e.stopPropagation();
              e.preventDefault();
              submit({ name: formData });
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
          autoFocus
        />
        <Button
          className={styles.button}
          press={() => submit({ name: formData })}
          isDisabled={formData.trim() === ""}
        >
          {t("listsList.tags.submit")}
        </Button>
      </div>
      {unUsedTags.length > 0 && (
        <ul className={styles.list}>
          {[...filteredTags]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((tag) => {
              const color = `#${intToRGB(hashCode(tag.name))}`;
              const textColor = isColorLight(color) ? "#000000" : "#FFFFFF";
              return (
                <li
                  key={tag.name}
                  className={styles.item}
                  onClick={() => submit(tag)}
                >
                  <span
                    className={styles.tagBadge}
                    style={{ backgroundColor: color, color: textColor }}
                  >
                    {tag.name}
                  </span>
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
}

export { TagForm };
